import { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Share2, Copy, Mail, Loader2, Trash2, Eye, Lock, Pencil, X, ShieldCheck } from "lucide-react";
import EncryptedSendDialog from "@/components/EncryptedSendDialog";
import { recordGuestShare } from "@/lib/guestShareSync";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useT } from "@/i18n/LanguageContext";
import { useAuth } from "@/auth/AuthContext";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

const emailOk = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

export default function ShareDialog({ note, open, onOpenChange }) {
  const { t } = useT();
  const { authState } = useAuth();
  const isGuest = authState !== "user";
  const [shares, setShares] = useState([]);
  const [email, setEmail] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [role, setRole] = useState("viewer");
  const [loading, setLoading] = useState(false);
  const [encryptedOpen, setEncryptedOpen] = useState(false);

  const reload = useCallback(async () => {
    // Guest notes don't live on the backend, so there's nothing to list.
    // Anonymous shares still work (POST /share-anonymous returns the url
    // which we copy to clipboard) but we can't enumerate past links.
    if (!note || isGuest) { setShares([]); return; }
    try {
      const { data } = await api.get(`/notes/${note.id}/shares`);
      setShares(data);
    } catch { setShares([]); }
  }, [note, isGuest]);

  useEffect(() => {
    if (!open || !note) { setEmail(""); setPassphrase(""); setRole("viewer"); return; }
    reload();
  }, [open, note, reload]);

  const createShare = async (withEmail) => {
    if (!note) return;
    if (withEmail && !emailOk(email.trim())) {
      toast.error(t("share.invalidEmail"));
      return;
    }
    try {
      setLoading(true);
      let data;
      if (isGuest) {
        // Guests send the full note content so the backend can persist an
        // orphan copy and return a shareable token. No account needed.
        const payload = {
          frontend_origin: window.location.origin,
          role,
          title: note.title || "",
          content: note.content || "",
          is_checklist: !!note.is_checklist,
          checklist_items: note.checklist_items || [],
          color: note.color || "default",
          images: note.images || [],
          attachments: note.attachments || [],
        };
        if (withEmail) payload.email = email.trim();
        if (passphrase.trim()) payload.passphrase = passphrase.trim();
        const resp = await api.post("/share-anonymous", payload);
        data = resp.data;
        recordGuestShare(note, data);
      } else {
        const payload = { frontend_origin: window.location.origin, role };
        if (withEmail) payload.email = email.trim();
        if (passphrase.trim()) payload.passphrase = passphrase.trim();
        const resp = await api.post(`/notes/${note.id}/share`, payload);
        data = resp.data;
      }
      await reload();
      if (withEmail && data.email_sent) {
        toast.success(t("share.sent"));
        setEmail("");
      } else {
        try {
          await navigator.clipboard.writeText(data.url);
          toast.success(t("share.linkCopied"));
        } catch {
          toast.success(t("share.linkCopied"));
        }
      }
      setPassphrase("");
    } catch (e) {
      // Surface the real reason so the user understands what failed.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const msg =
        status === 401 ? "Please sign in to share saved notes (or share this one without signing in — try again)"
      : status === 422 ? (Array.isArray(detail) ? (detail[0]?.msg || "Invalid input") : "Invalid email")
      : status === 429 ? "Too many share attempts — please wait a minute"
      : (typeof detail === "string" ? detail : t("share.failed"));
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const copyOne = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("share.linkCopied"));
    } catch {}
  };

  const revoke = async (token) => {
    try {
      await api.delete(`/shares/${token}`);
      toast.success(t("share.revokedToast"));
      reload();
    } catch { toast.error(t("share.failed")); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md overflow-hidden" data-testid="share-dialog">
        {/* Explicit close button — the shadcn DialogContent doesn't ship
            one by default, and mobile users had no way to dismiss "Share
            this note" without tapping the backdrop (which doesn't exist on
            tiny screens). The button is positioned top-right so it's
            reachable with one thumb. */}
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute right-3 top-3 h-9 w-9 inline-flex items-center justify-center rounded-full text-stone-600 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors z-10"
          data-testid="share-dialog-close"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Share2 className="h-5 w-5 text-amber-600" />
            {t("share.title")}
          </DialogTitle>
          <DialogDescription>{t("share.subtitle")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="font-mono-xs text-stone-500 dark:text-stone-400 mb-1.5 block">
              {t("share.roleLabel")}
            </label>
            <div className="grid grid-cols-2 gap-2 w-full min-w-0" data-testid="share-role-toggle">
              <button
                type="button"
                onClick={() => setRole("viewer")}
                data-testid="share-role-viewer"
                className={cn(
                  "rounded-xl border px-3 py-2.5 text-sm font-medium transition-all flex items-center gap-2 min-h-[46px] min-w-0 w-full",
                  role === "viewer"
                    ? "border-stone-900 dark:border-stone-100 bg-stone-50 dark:bg-stone-800"
                    : "border-stone-200 dark:border-stone-700 hover:border-stone-400"
                )}
              >
                <Eye className="h-4 w-4 shrink-0" />
                <span className="truncate min-w-0">{t("share.roleViewer")}</span>
              </button>
              <button
                type="button"
                onClick={() => setRole("editor")}
                data-testid="share-role-editor"
                className={cn(
                  "rounded-xl border px-3 py-2.5 text-sm font-medium transition-all flex items-center gap-2 min-h-[46px] min-w-0 w-full",
                  role === "editor"
                    ? "border-amber-500 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-200"
                    : "border-stone-200 dark:border-stone-700 hover:border-stone-400"
                )}
              >
                <Pencil className="h-4 w-4 shrink-0" />
                <span className="truncate min-w-0">{t("share.roleEditor")}</span>
              </button>
            </div>
          </div>

          <div>
            <label className="font-mono-xs text-stone-500 dark:text-stone-400 mb-1 block">
              {t("share.passphraseLabel")}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-stone-400" />
              <Input
                value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
                placeholder={t("share.passphrasePh")}
                className="pl-9"
                data-testid="share-passphrase-input"
                maxLength={64}
              />
            </div>
            {passphrase.trim() && (
              <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">{t("share.passphraseHint")}</p>
            )}
          </div>

          <Button
            onClick={() => createShare(false)}
            disabled={loading}
            className="w-full rounded-full h-11"
            variant="outline"
            data-testid="share-create-link-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Copy className="h-4 w-4 mr-2" />}
            {t("share.createLink")}
          </Button>

          <div className="flex items-center gap-2">
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("share.emailPlaceholder")}
              type="email"
              data-testid="share-email-input"
              onKeyDown={(e) => e.key === "Enter" && createShare(true)}
            />
            <Button
              onClick={() => createShare(true)}
              disabled={loading || !email.trim()}
              className="rounded-full shrink-0"
              data-testid="share-send-email-btn"
            >
              {loading ? <Loader2 className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        <Button
          variant="outline"
          onClick={() => setEncryptedOpen(true)}
          className="w-full rounded-full h-11 border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200"
          data-testid="share-encrypted-btn"
        >
          <ShieldCheck className="h-4 w-4 mr-2" />
          Send encrypted (password)
        </Button>
        <EncryptedSendDialog note={note} open={encryptedOpen} onOpenChange={setEncryptedOpen} />

        <div className="pt-3 border-t border-border">
          <p className="font-mono-xs text-stone-500 dark:text-stone-400 mb-2">
            {t("share.activeLinks")}
          </p>
          {shares.length === 0 ? (
            <p className="text-sm text-stone-500 dark:text-stone-400 italic py-2">
              {t("share.noLinks")}
            </p>
          ) : (
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {shares.map((s) => {
                const url = `${window.location.origin}/share/${s.token}`;
                return (
                  <div key={s.token} className="flex items-center gap-2 text-xs min-w-0" data-testid={`share-row-${s.token}`}>
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="truncate font-mono text-stone-700 dark:text-stone-300 flex items-center gap-1.5 min-w-0">
                        {s.requires_password && <Lock className="h-3 w-3 text-amber-600 shrink-0" />}
                        <span className="truncate min-w-0">{url}</span>
                      </div>
                      <div className="text-stone-500 dark:text-stone-400 flex items-center gap-2 mt-0.5">
                        {s.email && <span className="truncate">{s.email}</span>}
                        <span className="inline-flex items-center gap-0.5"><Eye className="h-3 w-3" /> {s.views || 0}</span>
                        <span>· {safeDate(s.created_at)}</span>
                        {s.requires_password && <span className="text-amber-700 dark:text-amber-300 font-medium">{t("share.locked")}</span>}
                        {s.role === "editor" && <span className="text-emerald-700 dark:text-emerald-300 font-medium">{t("share.editorBadge")}</span>}
                      </div>
                    </div>
                    <button
                      onClick={() => copyOne(url)}
                      className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-stone-100 dark:hover:bg-stone-800"
                      aria-label={t("share.copyLink")}
                      data-testid={`share-copy-${s.token}`}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => revoke(s.token)}
                      className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-rose-100 dark:hover:bg-rose-900/40 text-rose-600 dark:text-rose-300"
                      aria-label={t("share.revoke")}
                      data-testid={`share-revoke-${s.token}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function safeDate(iso) {
  try { return format(new Date(iso), "MMM d"); } catch { return ""; }
}
